import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

/**
 * Save one or more site settings, and refresh what depends on them.
 *
 * The counterpart to `useHomepageSectionMutation`. Quick Edit had two different
 * save paths: homepage sections went through a mutation with invalidation
 * attached, while every settings field hand-rolled
 * `api.put('/admin/settings', …)` followed by a manual
 * `invalidateQueries(['settings'])` at the call site — twenty of them, each an
 * opportunity to forget the second line and leave the storefront showing the
 * old value until a reload.
 *
 * One hook means the invalidation cannot be forgotten, and errors surface the
 * same way from both paths.
 */
export function useSiteSettingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: Record<string, unknown>) =>
      api.put('/admin/settings', { settings }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

/**
 * A save function shaped for `<QuickEditInline onSave={…}>`.
 *
 * Returns a promise that rejects on failure, which is what makes the inline
 * editor stay open and show the error rather than silently discarding the
 * typing — so the caller must not swallow it.
 */
export function useSaveSetting(): (key: string, value: unknown) => Promise<void> {
  const mutation = useSiteSettingMutation();
  return async (key, value) => {
    await mutation.mutateAsync({ [key]: value });
  };
}
